export enum ProductName {
  BusinessClass = 'Business Class',
  BusinessClass2PO = 'Business Class v2 PO',
  BusinessClass3PO = 'Business Class v3 PO',
  BusinessClass3x1PO = 'Business Class v3.1 PO',
  BusinessClass3x3PO = 'Business Class v3.3 PO',
  Enterprise = 'Enterprise',
  Enterprise1x1 = 'Enterprise v1.1',
  Enterprise1x2 = 'Enterprise v1.2',
  TrelloGoldFromBC = 'Trello Gold from BC',
  TrelloGoldMonthly = 'Trello Gold',
  TrelloGoldYearly = 'Trello Gold',
}

export enum ProductShortName {
  BusinessClassMonthly = 'businessClassMonthly',
  BusinessClassYearly = 'businessClassYearly',
  BusinessClass3Monthly = 'businessClass3Monthly',
  BusinessClass3Yearly = 'businessClass3Yearly',
  BusinessClass2PO = 'businessClass2PO',
  BusinessClass3PO = 'businessClass3PO',
  BusinessClass3x1Monthly = 'businessClass3x1Monthly',
  BusinessClass3x1Yearly = 'businessClass3x1Yearly',
  BusinessClass3x2Monthly = 'businessClass3x2Monthly',
  BusinessClass3x2Yearly = 'businessClass3x2Yearly',
  BusinessClass3x3Monthly = 'businessClass3x3Monthly',
  BusinessClass3x3Yearly = 'businessClass3x3Yearly',
  BusinessClass3x1PO = 'businessClass3x1PO',
  BusinessClass3x3PO = 'businessClass3x3PO',
  Enterprise = 'enterprise',
  Enterprise1x1TieredPricing = 'enterprise1x1TieredPricing',
  Enterprise1x2SiteLicense = 'enterprise1x2SiteLicense',
  TrelloGoldFromBC = 'trelloGoldFromBC',
  TrelloGoldMonthly = 'trelloGoldMonthly',
  TrelloGoldYearly = 'trelloGoldYearly',
}

export enum ProductInterval {
  Monthly = 'monthly',
  Yearly = 'yearly',
}

export enum MemberFeature {
  AdditionalStickers = 'additionalStickers',
  CustomStickers = 'customStickers',
  AdditionalBoardBackgrounds = 'additionalBoardBackgrounds',
  CustomBoardBackgrounds = 'customBoardBackgrounds',
  LargeAttachments = 'largeAttachments', // can upload larger attachments
  CustomEmoji = 'customEmoji',
  SavedSearches = 'savedSearches',
  Crown = 'crown',
  ReadSecrets = 'readSecrets', // Share a link that grants a read only view of a board or card
  ThreePlugins = 'threePlugins', // Can enable up to three plugins on any board
  InfinitePlugins = 'infinitePlugins',
}

export enum OrganizationFeature {
  Activity = 'activity', // Org admins can see members' last activity in the org
  CSVExport = 'csvExport', // Can generate a CSV export.
  Deactivated = 'deactivated', // Members can be deactivated from orgs to prevent read/write permissions temporarily
  EnterpriseUI = 'enterpriseUI', // The UI says it's enterprise
  Export = 'export', // Export org data in a zip file
  GoldMembers = 'goldMembers', // All members get gold
  GoogleApps = 'googleApps', // Org can be associated with a google apps domain
  InviteOrg = 'inviteOrg', // [PREF] Org-level option to restrict org invitations by domain name
  InviteBoard = 'inviteBoard', // [PREF] Org-level option to restrict board invitations by team membership or enterprise managed status
  Logo = 'logo', // Custom org logo
  Observers = 'observers', // Members can be made observers of boards
  OrgCards = 'orgCards', // Org users can see all visible members cards in the org
  OrgMembersPage = 'orgMembersPage', // Admins can use the org members page
  Plugins = 'plugins', // Plugin integrations are available
  Prefs = 'prefs', // Not an actual feature, just marks the ability to use the preferences menu
  Removal = 'removal', // Members being removed from the org can be taken off all boards and cards in the org
  RestrictVis = 'restrictVis', // [PREF] Org-level option to restrict board creation of a certain visibility
  ShortExportHistory = 'shortExportHistory', // Keep track of last 10 exports
  SuperAdmins = 'superAdmins', // Org admins have admin powers on all org boards
  Tags = 'tags', // Boards can be tagged with their organization's named tags
  MultiBoardGuests = 'multiBoardGuests', // Multi-board guests are billable members
  ToBeUpgradedToMBGPlan = 'toBeUpgradedToMBGPlan', // Organizations not yet enabled within multi-board guest feature
}

export type ProductFeatureSet = Set<MemberFeature | OrganizationFeature>;
export type ProductFeatureMap = Map<number, ProductFeatureSet>;

export interface ProductSpec {
  shortName: ProductShortName;
  name: ProductName;
  interval?: ProductInterval;
  price?: number;
  features: MemberFeatures | OrganizationFeatures;
  hidden?: boolean;
  perActiveUser?: boolean;
  perUser?: boolean;
  prebill?: boolean;
  purchaseOrder?: boolean;
  upgrade?: boolean;
  yearlyEquivalent?: number;
}

export type MemberFeatures = Set<MemberFeature>;
export type OrganizationFeatures = Set<OrganizationFeature>;

export type ProductSpecMap = Map<number, ProductSpec>;
export type ProductNameMap = Map<number, ProductName>;
export type ProductShortNameMap = Map<ProductShortName, number>;

export type ProductIdMap<T> = Map<number, T>;

export type ProductDesc = ProductShortName | string | number;

export interface BillingDates {
  [productCode: number]: string;
}

interface PerUserBillingStrategy {
  perUser: true;
  perActiveUser: boolean;
  billableCollaboratorCount: number;
  userCount: number;
}

interface FlatRateBillingStrategy {
  flatRate: true;
}

export type BillingStrategy = PerUserBillingStrategy | FlatRateBillingStrategy;

export const isFlatRateBillingStrategy = (
  strategy: BillingStrategy,
): strategy is FlatRateBillingStrategy => {
  return !!(strategy as FlatRateBillingStrategy).flatRate;
};
